/**
 * On-site recovery for the native agentic-cart checkout.
 *
 * Mirrors `checkout-recovery.ts` (the report-CTA flow) but is keyed on the
 * bundle slug instead of a report plan id, because an agentic cart is resumed
 * by re-entering the same slug-parameterized screen (`/cart/?bundle_id=...`) —
 * there is no per-cart route. The `cart_id` lives only inside the in-flight
 * Razorpay modal, so the durable resume key is the stable bundle slug.
 */

const CART_CHECKOUT_RECOVERY_STORAGE_KEY = "ishvaram_cart_checkout_recovery_v1";
const SUPPORT_EMAIL = "support@ishvaram.com";

export interface CartCheckoutRecoveryContext {
  bundleId: string;
  bundleName: string;
  priceInr: number;
  cartId?: string;
  razorpayOrderId?: string;
  sourcePath?: string;
  intent?: string;
  customerProblem?: string;
  journeyActionId?: string;
  concernId?: string;
  language?: "en" | "hi";
  customerEmail?: string;
  customerPhone?: string;
  resumeHref: string;
  createdAt: string;
}

export function buildCartRecoveryResumeHref({
  bundleId,
  sourcePath,
  intent,
  customerProblem,
  journeyActionId,
  concernId,
  language,
  origin,
}: {
  bundleId: string;
  sourcePath?: string;
  intent?: string;
  customerProblem?: string;
  journeyActionId?: string;
  concernId?: string;
  language?: "en" | "hi";
  origin?: string;
}): string {
  const baseOrigin =
    origin ||
    (typeof window !== "undefined" && window.location.origin
      ? window.location.origin
      : "https://ishvaram.com");
  const url = new URL("/cart/", baseOrigin);
  url.searchParams.set("bundle_id", bundleId);
  url.searchParams.set("checkout_recovery", "1");
  url.searchParams.set("recovery_channel", "support_email");
  if (sourcePath) url.searchParams.set("source", sourcePath);
  if (intent) url.searchParams.set("intent", intent);
  if (customerProblem) url.searchParams.set("customer_problem", customerProblem);
  if (journeyActionId) url.searchParams.set("journey_action_id", journeyActionId);
  if (concernId) url.searchParams.set("concern_id", concernId);
  if (language) url.searchParams.set("lang", language);
  return url.href;
}

export function buildCartCheckoutRecoveryContext({
  bundleId,
  bundleName,
  priceInr,
  cartId,
  razorpayOrderId,
  sourcePath,
  intent,
  customerProblem,
  journeyActionId,
  concernId,
  language,
  customerEmail,
  customerPhone,
}: Omit<CartCheckoutRecoveryContext, "createdAt" | "resumeHref">): CartCheckoutRecoveryContext {
  return {
    bundleId,
    bundleName,
    priceInr,
    cartId,
    razorpayOrderId,
    sourcePath,
    intent,
    customerProblem,
    journeyActionId,
    concernId,
    language,
    customerEmail,
    customerPhone,
    resumeHref: buildCartRecoveryResumeHref({
      bundleId,
      sourcePath,
      intent,
      customerProblem,
      journeyActionId,
      concernId,
      language,
    }),
    createdAt: new Date().toISOString(),
  };
}

export function saveCartCheckoutRecoveryContext(context: CartCheckoutRecoveryContext) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CART_CHECKOUT_RECOVERY_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // On-page recovery still works when storage is unavailable.
  }
}

export function readCartCheckoutRecoveryContext(): CartCheckoutRecoveryContext | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CART_CHECKOUT_RECOVERY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CartCheckoutRecoveryContext>;
    if (!parsed || typeof parsed.bundleId !== "string" || !parsed.bundleId) return null;
    return parsed as CartCheckoutRecoveryContext;
  } catch {
    return null;
  }
}

export function clearCartCheckoutRecoveryContext() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CART_CHECKOUT_RECOVERY_STORAGE_KEY);
  } catch {
    // Storage failure should not block checkout success.
  }
}

export function buildCartRecoverySupportEmailHref(context: CartCheckoutRecoveryContext): string {
  const subject = `Ishvaram checkout help: ${context.bundleName}`;
  const body = [
    "I was buying my Ishvaram upay plan but did not finish checkout.",
    `Plan: ${context.bundleName}`,
    context.customerProblem ? `Concern: ${context.customerProblem}` : "",
    context.cartId ? `Cart: ${context.cartId}` : "",
    context.razorpayOrderId ? `Order: ${context.razorpayOrderId}` : "",
    `Resume: ${context.resumeHref}`,
    "Please help me finish this checkout.",
  ]
    .filter(Boolean)
    .join("\n");
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
    body
  )}`;
}
